export class TabManager {
  constructor(containerSelector, lenisInstance = null) {
    this.container = document.querySelector(containerSelector);
    if (!this.container) {
      console.warn(`TabManager: Container not found: ${containerSelector}`);
      return;
    }

    this.lenis = lenisInstance || window.lenis;
    // .tab-button 또는 .no-category-link 모두 지원
    this.tabButtons = this.container.querySelectorAll(
      ".tab-button, .no-category-link"
    );
    this.tabContents = document.querySelectorAll(".tab-content");

    this.activeTab = null;
    this.activeContent = null;

    this.init();
  }

  init() {
    if (!this.container) return;

    // URL에서 탭 정보 읽기
    const urlParams = new URLSearchParams(window.location.search);
    const tabFromURL = urlParams.get("tab");

    // 초기 활성 탭 찾기 (URL 우선, 없으면 active 클래스, 없으면 첫 번째)
    let initialTab = null;

    if (tabFromURL) {
      // URL에 있는 탭이 유효한지 확인
      const isValidTab = Array.from(this.tabButtons).some(
        (btn) => btn.getAttribute("data-tab") === tabFromURL
      );
      if (isValidTab) {
        initialTab = tabFromURL;
      }
    }

    if (!initialTab) {
      // URL에 없으면 active 클래스 확인
      const activeButton = this.container.querySelector(
        ".tab-button.active, .no-category-link.active"
      );
      if (activeButton) {
        initialTab = activeButton.getAttribute("data-tab");
      } else if (this.tabButtons.length > 0) {
        // active 클래스가 없으면 첫 번째 탭
        const firstButton = this.tabButtons[0];
        initialTab = firstButton.getAttribute("data-tab");
      }
    }

    // 초기 탭 활성화
    if (initialTab) {
      this.activateTab(initialTab, false); // 초기화 시 스크롤 없음

      // URL에 탭 정보가 없으면 초기 탭으로 URL 설정 (replaceState 사용하여 히스토리에 추가하지 않음)
      if (!tabFromURL) {
        this.updateURL(initialTab, true); // replaceState 사용
      }
    }

    // 탭 버튼 클릭 이벤트
    this.tabButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const tabName = button.getAttribute("data-tab");
        if (tabName && tabName !== this.activeTab) {
          this.activateTab(tabName, true); // 클릭 시 스크롤
          this.updateURL(tabName); // URL 업데이트
        }
      });
    });

    // 브라우저 뒤로/앞으로 가기 처리
    window.addEventListener("popstate", (e) => {
      const urlParams = new URLSearchParams(window.location.search);
      const tabFromURL = urlParams.get("tab");
      if (tabFromURL && tabFromURL !== this.activeTab) {
        this.activateTab(tabFromURL, false); // 히스토리 이동 시 스크롤 없음
      } else if (!tabFromURL && this.activeTab) {
        // 쿼리스트링이 없으면 첫 번째 탭으로
        const firstButton = this.tabButtons[0];
        if (firstButton) {
          const firstTabName = firstButton.getAttribute("data-tab");
          this.activateTab(firstTabName, false);
        }
      }
    });
  }

  activateTab(tabName, scrollToContent = true) {
    if (!tabName) return;

    // 모든 탭 버튼에서 active 제거
    this.tabButtons.forEach((btn) => {
      btn.classList.remove("active");
    });

    // 활성 탭 버튼 찾기 및 active 추가
    const activeButton = Array.from(this.tabButtons).find(
      (btn) => btn.getAttribute("data-tab") === tabName
    );
    if (activeButton) {
      activeButton.classList.add("active");
      this.activeTab = tabName;
    }

    // 모든 탭 콘텐츠 처리
    this.tabContents.forEach((content) => {
      const contentTab = content.getAttribute("data-tab-content");

      if (contentTab === tabName) {
        // 활성 콘텐츠 표시 (먼저 is-active 클래스 추가)
        this.activeContent = content;
        this.showContent(content);

        // 스크롤 처리 (is-active 클래스가 적용된 후, 레이아웃이 안정화된 후 실행)
        if (scrollToContent && this.lenis) {
          // requestAnimationFrame을 사용하여 DOM 업데이트 후 실행
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.scrollToContent(content);
            });
          });
        }
      } else {
        // 비활성 콘텐츠 숨기기
        this.hideContent(content);
      }
    });
  }

  showContent(content) {
    if (!content) return;

    // is-active 클래스 추가 (SCSS에서 스타일 처리)
    content.classList.add("is-active");

    // AOS refresh (탭 변경 시 새로운 콘텐츠의 애니메이션을 다시 초기화)
    if (window.AOS) {
      requestAnimationFrame(() => {
        window.AOS.refresh();
      });
    }
  }

  hideContent(content) {
    if (!content) return;

    // is-active 클래스 제거 (SCSS에서 스타일 처리)
    content.classList.remove("is-active");
  }

  scrollToContent(content) {
    if (!content || !this.lenis) return;

    // 콘텐츠가 표시될 때까지 약간의 지연 (레이아웃 계산 완료 후)
    // 여러 requestAnimationFrame을 사용하여 레이아웃이 완전히 안정화된 후 스크롤
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // 콘텐츠의 상단 위치 계산
        const contentRect = content.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const contentTop = contentRect.top + scrollTop;

        // 헤더 높이를 고려한 오프셋 (CSS 변수에서 가져오기)
        const headerHeight =
          parseInt(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--header-height"
            )
          ) || 80;

        // 콘텐츠의 상단으로 스크롤 (헤더 높이만큼 오프셋)
        const targetY = contentTop - headerHeight;

        // Lenis로 부드럽게 스크롤
        if (this.lenis && typeof this.lenis.scrollTo === "function") {
          this.lenis.scrollTo(targetY, {
            duration: 1.2, // 스크롤 애니메이션 시간 (초)
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Lenis 기본 easing
            offset: 0, // 추가 오프셋 (픽셀)
          });
        }
      });
    });
  }

  // 외부에서 탭 변경 가능하도록 public 메서드
  switchTab(tabName) {
    this.activateTab(tabName, true);
  }

  // 현재 활성 탭 가져오기
  getActiveTab() {
    return this.activeTab;
  }

  // URL 쿼리스트링 업데이트
  updateURL(tabName, replace = false) {
    if (!tabName) return;

    const url = new URL(window.location);

    // tab 쿼리스트링 추가 또는 업데이트
    url.searchParams.set("tab", tabName);

    // 히스토리 업데이트
    if (replace) {
      // replaceState: 히스토리에 추가하지 않고 현재 항목만 교체 (초기 로드 시 사용)
      window.history.replaceState({ tab: tabName }, "", url);
    } else {
      // pushState: 히스토리에 추가 (탭 클릭 시 사용, 뒤로 가기 가능)
      window.history.pushState({ tab: tabName }, "", url);
    }
  }

  // URL에서 탭 정보 읽기
  getTabFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("tab");
  }
}
